import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowRight, Search, Tag, Loader2, RefreshCw, FileText } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useScrollToTopOnMount } from '../hooks/useScrollToTop';
import AdminEditButton from '../components/AdminEditButton';

// Custom Link component that scrolls to top
const ScrollToTopLink = ({ to, children, className, ...props }: { to: string; children: React.ReactNode; className?: string; [key: string]: any }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Scroll to top first
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Then navigate after a short delay to ensure scroll completes
    setTimeout(() => {
      navigate(to);
    }, 100);
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

const BlogList = () => {
  const { 
    blogPosts, 
    loading, 
    blogPostsLoading, 
    blogPostsHasMore, 
    blogPostsTotal,
    loadPublishedBlogPosts,
    loadMoreBlogPosts,
    searchBlogPosts,
    filterBlogPostsByCategory,
    clearBlogPostsCache
  } = useData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchHasMore, setSearchHasMore] = useState(false);

  // Filter published blog posts for display
  const publishedPosts = blogPosts.filter(post => post.status === 'published');

  // Get unique categories from published posts
  const categories = useMemo(() => {
    return Array.from(
      new Set(publishedPosts.flatMap(post => post.categories || []))
    );
  }, [publishedPosts]);

  // Load initial blog posts
  useEffect(() => {
    const loadInitialPosts = async () => {
      try {
        clearBlogPostsCache();
        const { posts } = await loadPublishedBlogPosts(1, 18, true);
        // The posts will be set in the context
      } catch (error) {
        console.error('Error loading initial blog posts:', error);
      }
    };

    loadInitialPosts();
  }, [loadPublishedBlogPosts, clearBlogPostsCache]);

  // Handle search
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchHasMore(false);
      return;
    }

    setIsSearching(true);
    try {
      const { posts, hasMore } = await searchBlogPosts(query, 1, 18);
      setSearchResults(posts);
      setSearchHasMore(hasMore);
    } catch (error) {
      console.error('Error searching blog posts:', error);
    } finally {
      setIsSearching(false);
    }
  }, [searchBlogPosts]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);

  // Handle category filter
  const handleCategoryChange = useCallback(async (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    
    if (category === 'all') {
      // Reset to all posts
      clearBlogPostsCache();
      const { posts } = await loadPublishedBlogPosts(1, 18, true);
      return;
    }

    try {
      const { posts, hasMore } = await filterBlogPostsByCategory(category, 1, 18);
      setSearchResults(posts);
      setSearchHasMore(hasMore);
    } catch (error) {
      console.error('Error filtering by category:', error);
    }
  }, [filterBlogPostsByCategory, clearBlogPostsCache, loadPublishedBlogPosts]);

  // Infinite scroll handler
  const handleLoadMore = useCallback(async () => {
    if (searchTerm.trim()) {
      // Load more search results
      const nextPage = Math.ceil(searchResults.length / 18) + 1;
      const { posts, hasMore } = await searchBlogPosts(searchTerm, nextPage, 18);
      setSearchResults(prev => [...prev, ...posts]);
      setSearchHasMore(hasMore);
    } else if (selectedCategory !== 'all') {
      // Load more category results
      const nextPage = Math.ceil(searchResults.length / 18) + 1;
      const { posts, hasMore } = await filterBlogPostsByCategory(selectedCategory, nextPage, 18);
      setSearchResults(prev => [...prev, ...posts]);
      setSearchHasMore(hasMore);
    } else {
      // Load more from main posts
      await loadMoreBlogPosts();
    }
  }, [searchTerm, selectedCategory, searchResults.length, searchBlogPosts, filterBlogPostsByCategory, loadMoreBlogPosts]);

  // Determine which posts to display
  const displayPosts = useMemo(() => {
    if (searchTerm.trim() || selectedCategory !== 'all') {
      return searchResults;
    }
    return publishedPosts;
  }, [searchTerm, selectedCategory, searchResults, publishedPosts]);

  // Determine if we should show load more button
  const shouldShowLoadMore = useMemo(() => {
    if (searchTerm.trim() || selectedCategory !== 'all') {
      return searchHasMore;
    }
    return blogPostsHasMore;
  }, [searchTerm, selectedCategory, searchHasMore, blogPostsHasMore]);

  // Update document title
  useEffect(() => {
    document.title = 'Blog - Simran Industries | Cattle Feed & Nutrition Insights';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read expert insights on cattle feed, dairy nutrition, and livestock management from Simran Industries. Stay updated with the latest in animal nutrition.');
    }
  }, []);

  // Scroll to top when component mounts
  useScrollToTopOnMount();

  const getContentPreview = (content: string, maxLength: number = 150) => {
    const textContent = content.replace(/<[^>]*>/g, '');
    return textContent.length > maxLength 
      ? textContent.substring(0, maxLength) + '...'
      : textContent;
  };

  // Loading skeleton component
  const BlogPostSkeleton = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-6">
        <div className="flex gap-2 mb-3">
          <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded mb-3"></div>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-4"></div>
        <div className="flex justify-between items-center">
          <div className="h-4 w-24 bg-gray-200 rounded"></div>
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  if (loading && displayPosts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="mx-auto text-amber-600 mb-4 animate-spin" size={48} />
            <p className="text-gray-600 text-lg">Loading blog posts...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Admin Edit Button - Only visible to logged-in admins */}
      <AdminEditButton type="blog" id="list" className="top-32" />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white py-20 pt-32">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Cattle Feed & Nutrition Blog
          </h1>
          <p className="text-xl text-yellow-100 max-w-3xl mx-auto leading-relaxed">
            Expert insights, tips, and best practices for dairy farming, cattle nutrition, 
            and livestock management from the team at Simran Industries.
          </p>
          {blogPostsTotal > 0 && (
            <p className="text-yellow-200 mt-4">
              {blogPostsTotal} articles available
            </p>
          )}
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" size={20} />
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category: string) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {displayPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {displayPosts.map((post) => (
                  <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group relative">
                    {/* Admin Edit Button for this post */}
                    <AdminEditButton type="blog" id={post.id} variant="card" size="sm" />
                    
                    {/* Featured Image */}
                    {post.featuredImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={post.featuredImage} 
                          alt={post.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                      </div>
                    )}
                    
                    <div className="p-6">
                      {/* Categories */}
                      {post.categories && post.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.categories.slice(0, 2).map((category: string, index: number) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            >
                              <Tag size={12} className="mr-1" />
                              {category}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        <ScrollToTopLink 
                          to={`/${post.slug}`}
                          className="hover:text-amber-600 transition-colors"
                        >
                          {post.title}
                        </ScrollToTopLink>
                      </h2>
                      
                      {/* Excerpt */}
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt || getContentPreview(post.content)}
                      </p>
                      
                      {/* Meta Info */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>
                              {new Date(post.publishedAt || post.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <User size={14} />
                            <span>Simran Industries</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Read More Link */}
                      <ScrollToTopLink
                        to={`/${post.slug}`}
                        className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-medium transition-colors"
                      >
                        <span>Read More</span>
                        <ArrowRight size={16} />
                      </ScrollToTopLink>
                    </div>
                  </article>
                ))}
              </div>

              {/* Load More Button */}
              {shouldShowLoadMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={handleLoadMore}
                    disabled={blogPostsLoading}
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {blogPostsLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw size={20} />
                        <span>Load More Posts</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="text-gray-500 text-lg mb-4">
                {searchTerm.trim() ? (
                  <>
                    <Search className="mx-auto mb-4" size={48} />
                    <p>No blog posts found for "{searchTerm}"</p>
                    <p className="text-sm mt-2">Try adjusting your search terms</p>
                  </>
                ) : (
                  <>
                    <FileText className="mx-auto mb-4" size={48} />
                    <p>No blog posts available</p>
                    <p className="text-sm mt-2">Check back later for new content</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Loading skeletons for infinite scroll */}
          {blogPostsLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
              {[...Array(8)].map((_, index) => (
                <BlogPostSkeleton key={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;